import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay, A11y } from "swiper/modules";
import PropTypes from "prop-types";
import { IoClose, IoDocumentText, IoNewspaper, IoVideocam, IoSchool } from "react-icons/io5";
import "./../../styles/SwiperCarousel.css";
import bannerService from "../../services/BannerService";
import postsService from "../../services/PostsService";
import { useAuth } from "../../context/AuthContext";
import { useWelcomeCourseStatus } from "../../hooks/useWelcomeCourseStatus";
import { recordEnrollment } from "../../utils/recentEnrollments";
import { stripHtml } from "../../utils/stingUtils";

const SwiperCarousel = ({ 
  items, 
  spaceBetween = 0,
  autoplayDelay = 5000,
  showNavigation = true,
  showPagination = true,
  loop = true,
  centeredSlides = true,
  onLoginRequired,
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const {
    canAccessOtherCourses,
    courseId: welcomeCourseId,
  } = useWelcomeCourseStatus();
  const welcomeBlocked = !canAccessOtherCourses;

  const requestLogin = () => {
    if (typeof onLoginRequired === 'function') onLoginRequired();
    else navigate('/');
  };

  const [bannerItems, setBannerItems] = useState([]);
  const [publicPosts, setPublicPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [postContent, setPostContent] = useState(null);
  const [isLoadingPost, setIsLoadingPost] = useState(false);
  const [modalSession, setModalSession] = useState(0);
  const [modalError, setModalError] = useState('');
  const [postCacheById, setPostCacheById] = useState({});
  const [courseModal, setCourseModal] = useState(null);
  const [enrollingCourse, setEnrollingCourse] = useState(false);
  const [courseAlert, setCourseAlert] = useState('');

  const normalizePostsData = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.posts)) return data.posts;
    return [];
  };

  const normalizePost = (post) => {
    if (!post) return null;

    const categoryRaw = post.category || post.Category || "";
    const category = String(categoryRaw).toLowerCase();

    let type = "noticia";
    if (category.includes("video")) type = "video";
    if (category.includes("ebook") || category.includes("e-book") || category.includes("documento") || category.includes("pdf")) {
      type = "pdf";
    }

    const toUrl = (path) => {
      if (!path || typeof path !== "string") return "";
      if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:")) return path;
      const cleanPath = path.startsWith("/") ? path : `/${path}`;
      return `${process.env.REACT_APP_LOCATION || ""}${cleanPath}`;
    };

    return {
      ...post,
      id: post.id ?? post.Id,
      type,
      title: post.title || post.Title || "",
      description: post.description || post.Description || "",
      thumbnail: toUrl(post.thumbnail || post.Thumbnail),
      videoUrl: toUrl(post.videoUrl || post.path || post.Path || post.videoPath),
      fileUrl: toUrl(post.fileUrl || post.path || post.Path || post.filePath),
      createdAt: post.createdAt || post.CreatedAt || post.createdDate,
    };
  };

  const ordenDe = (b) => Number(b?.orden ?? b?.Orden ?? 0);

  const sortByOrden = (list = []) =>
    [...list].sort((a, b) => {
      const d = ordenDe(a) - ordenDe(b);
      if (d !== 0) return d;
      return Number(a?.id ?? a?.Id ?? 0) - Number(b?.id ?? b?.Id ?? 0);
    });

  const normalizeBannerItem = (item = {}) => ({
    ...item,
    id: item.id ?? item.Id,
    orden: ordenDe(item),
    linkedPostId:
      item.linkedPostId ??
      item.postId ??
      item.PostId ??
      item.postID ??
      item.publicationId ??
      item.PublicationId ??
      item.post?.id ??
      item.Post?.id ??
      null,
    linkedCourseId:
      item.linkedCourseId ??
      item.courseId ??
      item.CourseId ??
      null,
    linkedCourseTitle:
      item.linkedCourseTitle ??
      item.courseTitle ??
      item.CourseTitle ??
      null,
  });

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setLoading(true);
        const banners = await bannerService.getActiveBanners();
        const sortedBanners = sortByOrden(banners || []);
        
        // Transformar los banners al formato esperado por el carrusel (orden ascendente: menor primero)
        const transformedBanners = sortedBanners.map((banner) => {
          let imageUrl = "/img/default.png";
          
          if (banner.imagePath) {
            // Si ya es una URL completa, usarla directamente
            if (banner.imagePath.startsWith('http://') || banner.imagePath.startsWith('https://')) {
              imageUrl = banner.imagePath;
            } else {
              // Construir la URL con REACT_APP_LOCATION
              const cleanPath = banner.imagePath.startsWith('/') ? banner.imagePath : `/${banner.imagePath}`;
              imageUrl = `${process.env.REACT_APP_LOCATION || ''}${cleanPath}`;
            }
          }
          
          return normalizeBannerItem({
            id: banner.id ?? banner.Id,
            image: imageUrl,
            title: banner.text || banner.Text || 'Banner',
            description: banner.text || banner.Text || '',
            orden: ordenDe(banner),
            linkedPostId:
              banner.postId ??
              banner.PostId ??
              banner.postID ??
              banner.publicationId ??
              banner.PublicationId ??
              banner.post?.id ??
              banner.Post?.id ??
              null,
            linkedCourseId: banner.courseId ?? banner.CourseId ?? null,
            linkedCourseTitle: banner.courseTitle ?? banner.CourseTitle ?? null,
          });
        });
        
        setBannerItems(transformedBanners);
      } catch (err) {
        console.error('Error al cargar banners:', err);
        setError(err);
        // Si hay error, usar items pasados como props si existen
        if (items && Array.isArray(items) && items.length > 0) {
          setBannerItems(sortByOrden(items).map(normalizeBannerItem));
        }
      } finally {
        setLoading(false);
      }
    };

    // Si se pasan items como props, ordenarlos también (Home ya puede venir ordenado)
    if (items && Array.isArray(items) && items.length > 0) {
      setBannerItems(sortByOrden(items).map(normalizeBannerItem));
      setLoading(false);
    } else {
      // Si no hay items, obtener desde el API
      fetchBanners();
    }
  }, [items]);

  useEffect(() => {
    const fetchPublicPosts = async () => {
      try {
        const response = await postsService.getPublicPosts();
        const normalized = normalizePostsData(response).map(normalizePost).filter(Boolean);
        setPublicPosts(normalized);
      } catch (publicError) {
        // Si falla el endpoint público (ej. CORS), no intentamos privado para evitar error de token.
        console.error("Error al cargar publicaciones para banners:", publicError);
        setPublicPosts([]);
      }
    };

    fetchPublicPosts();
  }, []);

  const openPostModal = async (postId) => {
    if (!postId) return;

    const normalizedPostId = Number(postId);
    const cachedPost = postCacheById[normalizedPostId];
    if (cachedPost) {
      setModalError('');
      setPostContent({ ...cachedPost });
      setIsLoadingPost(false);
      setIsPostModalOpen(true);
      setModalSession((prev) => prev + 1);
      return;
    }

    setModalError('');
    setPostContent(null);
    setIsLoadingPost(true);
    setIsPostModalOpen(true);
    setModalSession((prev) => prev + 1);

    const foundPost = publicPosts.find((post) => Number(post.id) === normalizedPostId);
    if (foundPost) {
      setPostContent({ ...foundPost });
      setPostCacheById((prev) => ({ ...prev, [normalizedPostId]: { ...foundPost } }));
      setIsLoadingPost(false);
      return;
    }

    try {
      const response = await postsService.getPublicPostById(normalizedPostId);
      const normalizedPost = normalizePost(response);
      if (normalizedPost) {
        setPostContent({ ...normalizedPost });
        setPostCacheById((prev) => ({ ...prev, [normalizedPostId]: { ...normalizedPost } }));
      } else {
        setModalError('No se pudo cargar la publicación.');
      }
    } catch (postError) {
      console.error("No se pudo cargar la publicacion vinculada del banner:", postError);
      setModalError('No se pudo cargar la publicación. Verifica CORS del endpoint público.');
    } finally {
      setIsLoadingPost(false);
    }
  };

  const closePostModal = () => {
    setIsPostModalOpen(false);
    setPostContent(null);
    setIsLoadingPost(false);
    setModalError('');
  };

  const openCourseModal = (banner) => {
    if (!banner?.linkedCourseId || !banner?.id) return;
    setCourseAlert('');
    setCourseModal({
      bannerId: banner.id,
      courseId: banner.linkedCourseId,
      courseTitle: stripHtml(banner.linkedCourseTitle) || stripHtml(banner.title) || 'Curso',
      image: banner.image,
      title: stripHtml(banner.title) || banner.title,
    });
  };

  const closeCourseModal = () => {
    setCourseModal(null);
    setCourseAlert('');
    setEnrollingCourse(false);
  };

  const handleBannerClick = (item) => {
    if (item.linkedCourseId) {
      openCourseModal(item);
      return;
    }
    if (item.linkedPostId) {
      openPostModal(item.linkedPostId);
    }
  };

  const handleEnrollFromBanner = async () => {
    if (!courseModal?.bannerId || !courseModal?.courseId) return;

    if (!isAuthenticated) {
      closeCourseModal();
      requestLogin();
      return;
    }

    if (welcomeBlocked && String(courseModal.courseId) !== String(welcomeCourseId)) {
      setCourseAlert('Debes completar el curso de bienvenida antes de inscribirte en otros cursos.');
      return;
    }

    try {
      setEnrollingCourse(true);
      const result = await bannerService.enrollLinkedCourse(courseModal.bannerId);
      const enrolledId = result?.courseId || courseModal.courseId;
      const enrolledTitle =
        stripHtml(result?.courseTitle) || stripHtml(courseModal.courseTitle) || 'curso';
      recordEnrollment(enrolledId);
      closeCourseModal();
      navigate(`/courses/Introcourse/${enrolledId}`, {
        state: {
          objeto: enrolledId,
          enrollMessage: result?.alreadyEnrolled
            ? `Ya estabas inscrito en "${enrolledTitle}".`
            : `Te has inscrito correctamente al curso "${enrolledTitle}".`,
        },
      });
    } catch (enrollError) {
      const message =
        enrollError?.response?.data?.message
        || enrollError?.response?.data
        || 'No se pudo completar la inscripción al curso.';
      setCourseAlert(typeof message === 'string' ? message : 'No se pudo completar la inscripción al curso.');
    } finally {
      setEnrollingCourse(false);
    }
  };

  const handleGoToCourseFromBanner = () => {
    if (!courseModal?.courseId) return;
    if (!isAuthenticated) {
      closeCourseModal();
      requestLogin();
      return;
    }
    const courseId = courseModal.courseId;
    closeCourseModal();
    navigate(`/courses/Introcourse/${courseId}`, { state: { objeto: courseId } });
  };

  // Mostrar loading
  if (loading) {
    return (
      <div className="swiper-compact-container flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full border-b-2 animate-spin border-green-aqua"></div>
          <p className="text-gray-600">Cargando banners...</p>
        </div>
      </div>
    );
  }

  // Verificar que hay elementos para mostrar
  if (!bannerItems || !Array.isArray(bannerItems) || bannerItems.length === 0) {
    return (
      <div className="swiper-compact-container flex items-center justify-center min-h-[400px]">
        <div className="text-center text-gray-500">
          <p>No hay banners disponibles</p>
        </div>
      </div>
    );
  }

  return (
    <div className="swiper-compact-container">
      <Swiper
        modules={[Navigation, Pagination, Autoplay, A11y]}
        spaceBetween={spaceBetween}
        slidesPerView={1}
        centeredSlides={centeredSlides}
        navigation={showNavigation ? {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        } : false}
        pagination={showPagination ? { 
          clickable: true,
          el: '.swiper-pagination',
          type: 'bullets'
        } : false}
        autoplay={{ 
          delay: autoplayDelay,
          pauseOnMouseEnter: true
        }}
        loop={loop}
        a11y={{
          prevSlideMessage: 'Diapositiva anterior',
          nextSlideMessage: 'Diapositiva siguiente',
          firstSlideMessage: 'Esta es la primera diapositiva',
          lastSlideMessage: 'Esta es la última diapositiva',
          paginationBulletMessage: 'Ir a la diapositiva {{index}}'
        }}
        grabCursor={true}
        className="compact-swiper"
      >
        {bannerItems.map((item, index) => {
          const isClickable = Boolean(item.linkedCourseId || item.linkedPostId);
          return (
            <SwiperSlide key={item.id || `banner-${index}`}>
              <div className="compact-slide">
                {isClickable ? (
                  <button
                    type="button"
                    className="compact-image-container cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBannerClick(item);
                    }}
                    aria-label={
                      item.linkedCourseId
                        ? `Inscribirse al curso de ${item.title || "banner"}`
                        : `Abrir publicación de ${item.title || "banner"}`
                    }
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      className="compact-image"
                      loading={index < 2 ? "eager" : "lazy"}
                      onError={(e) => {
                        console.error('Error cargando imagen:', item.image, e);
                      }}
                    />
                  </button>
                ) : (
                  <div className="compact-image-container">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="compact-image"
                      loading={index < 2 ? "eager" : "lazy"}
                      onError={(e) => {
                        console.error('Error cargando imagen:', item.image, e);
                      }}
                    />
                  </div>
                )}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      {showNavigation && (
        <>
          <div className="swiper-button-prev compact-button"></div>
          <div className="swiper-button-next compact-button"></div>
        </>
      )}
      
      {showPagination && (
        <div className="swiper-pagination compact-pagination"></div>
      )}

      {isPostModalOpen && (
        <div
          key={modalSession}
          className="fixed inset-0 z-[200] flex justify-center items-center p-4 bg-black bg-opacity-80 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePostModal();
          }}
        >
          <div
            className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b-2 border-gray-100">
              <div className="flex items-center space-x-2 px-3 py-1.5 bg-[#4DABA4] bg-opacity-10 rounded-lg">
                {postContent?.type === "video" && <IoVideocam className="text-lg text-[#4DABA4]" />}
                {postContent?.type === "pdf" && <IoDocumentText className="text-lg text-[#4DABA4]" />}
                {postContent?.type === "noticia" && <IoNewspaper className="text-lg text-[#4DABA4]" />}
                <span className="text-xs font-semibold text-[#4DABA4] uppercase tracking-wide">
                  {postContent?.type === "video" ? "Video" : postContent?.type === "pdf" ? "Documento" : "Noticia"}
                </span>
              </div>
              <button
                onClick={closePostModal}
                className="p-2.5 text-gray-500 rounded-xl transition-all duration-200 hover:text-gray-700 hover:bg-gray-100"
                aria-label="Cerrar"
              >
                <IoClose className="text-2xl" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-6">
              {isLoadingPost ? (
                <div className="flex flex-col justify-center items-center py-20">
                  <div className="w-14 h-14 border-4 border-[#4DABA4] border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-sm font-medium text-gray-500">Cargando publicación...</p>
                </div>
              ) : !postContent ? (
                <div className="flex justify-center items-center py-20">
                  <p className="text-gray-500">
                    {modalError || "No se pudo cargar la publicación."}
                  </p>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {postContent.title || "Sin titulo"}
                  </h2>

                  {postContent.type === "video" && postContent.videoUrl && (
                    <div className="flex relative justify-center items-center w-full bg-black rounded-xl overflow-hidden aspect-video mb-6">
                      <video src={postContent.videoUrl} controls autoPlay className="object-contain w-full h-full" preload="auto">
                        Tu navegador no soporta el elemento de video.
                      </video>
                    </div>
                  )}

                  {postContent.type === "pdf" && postContent.fileUrl && (
                    <div className="w-full bg-white rounded-xl overflow-hidden border border-gray-200 mb-6 aspect-[9/16]">
                      <iframe
                        src={`${postContent.fileUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                        className="w-full h-full border-0"
                        title="PDF Viewer"
                      />
                    </div>
                  )}

                  {postContent.type === "noticia" && (
                    <div className="mb-6">
                      <div className="overflow-hidden relative w-full bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl shadow-xl aspect-video mb-5">
                        <img
                          src={postContent.thumbnail || "/img/default.png"}
                          alt={postContent.title || "Noticia"}
                          className="object-cover w-full h-full"
                          onError={(e) => {
                            e.target.src = "/img/default.png";
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <p className="text-gray-700 whitespace-pre-line">
                    {postContent.description || "Sin descripcion disponible"}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {courseModal && (
        <div
          className="fixed inset-0 z-[200] flex justify-center items-center p-4 bg-black bg-opacity-80 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeCourseModal();
          }}
        >
          <div
            className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-5 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
              <div className="flex items-center space-x-2 px-3 py-1.5 bg-[#4DABA4] bg-opacity-10 rounded-lg">
                <IoSchool className="text-lg text-[#4DABA4]" />
                <span className="text-xs font-semibold text-[#4DABA4] uppercase tracking-wide">
                  Curso vinculado
                </span>
              </div>
              <button
                onClick={closeCourseModal}
                className="p-2 text-gray-500 rounded-xl transition-all duration-200 hover:text-gray-700 hover:bg-gray-100"
                aria-label="Cerrar"
              >
                <IoClose className="text-2xl" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {courseModal.image && (
                <div className="overflow-hidden rounded-xl bg-gray-100 aspect-video">
                  <img
                    src={courseModal.image}
                    alt={courseModal.title || courseModal.courseTitle}
                    className="object-cover w-full h-full"
                  />
                </div>
              )}

              <div>
                <p className="text-xs font-semibold tracking-wide text-[#4DABA4] uppercase mb-1">
                  Inscripción
                </p>
                <h3 className="text-xl font-bold text-gray-900">
                  {courseModal.courseTitle || 'Curso disponible'}
                </h3>
                {courseModal.title && courseModal.title !== courseModal.courseTitle && (
                  <p className="mt-1 text-sm text-gray-500">{courseModal.title}</p>
                )}
              </div>

              {courseAlert && (
                <div className="px-3 py-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg">
                  {courseAlert}
                </div>
              )}

              <div className="flex flex-col gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleEnrollFromBanner}
                  disabled={enrollingCourse}
                  className="w-full px-4 py-2.5 text-sm font-semibold text-white rounded-xl transition-all duration-200 background-green-aqua hover:shadow-lg hover:scale-[1.02] disabled:opacity-60"
                >
                  {enrollingCourse
                    ? 'Inscribiendo...'
                    : (isAuthenticated ? 'Inscribirme al curso' : 'Inicia sesión para inscribirte')}
                </button>
                <button
                  type="button"
                  onClick={handleGoToCourseFromBanner}
                  disabled={enrollingCourse}
                  className="w-full px-4 py-2.5 text-sm font-semibold text-[#4DABA4] bg-white border-2 border-[#4DABA4]/40 rounded-xl transition-all duration-200 hover:bg-[#4DABA4]/5 disabled:opacity-60"
                >
                  Ver curso
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

SwiperCarousel.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      linkedPostId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      linkedCourseId: PropTypes.string,
      linkedCourseTitle: PropTypes.string,
    })
  ),
  spaceBetween: PropTypes.number,
  autoplayDelay: PropTypes.number,
  showNavigation: PropTypes.bool,
  showPagination: PropTypes.bool,
  loop: PropTypes.bool,
  centeredSlides: PropTypes.bool,
  onLoginRequired: PropTypes.func,
};

export default SwiperCarousel;